import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import {
  SHARED_AXIS_KEY,
  type TrendPreview,
  type TrendPreviewYRanges,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/trendPreview'
import {
  chartPenId,
  computedTrendKey,
  type ComputedTrendRecord,
  saveComputedTrend,
} from 'features/timeSeries/computed/computedTrendStore'
import {
  type EmbeddedTrendView,
  useEmbeddedTrendWorkbench,
} from 'features/timeSeries/computed/EmbeddedWorkbenchContext'
import { useOpenComputedTrendResource } from 'features/timeSeries/computed/useOpenComputedTrendResource'
import { ExternalLink, X } from 'lucide-react'
import React, { useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'

const PanelRoot = styled.div`
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 0.0625rem solid var(--border);
`

const ChartArea = styled.div`
  flex: 1;
  min-height: 0;
`

const ActionFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  padding: 1rem;
  border-top: 1px solid var(--border);
  background: ${({ theme }) => theme.forecast.pageBackground1};
`

function chartAxisRanges(
  yRanges: TrendPreviewYRanges | undefined
): Pick<ComputedTrendRecord, 'customAxisRanges' | 'sharedAxisRange'> {
  if (!yRanges) return {}
  const { [SHARED_AXIS_KEY]: shared, ...perPen } = yRanges
  const customAxisRanges = Object.fromEntries(
    Object.entries(perPen).map(([id, range]) => [chartPenId(id), range])
  )
  return {
    ...(Object.keys(customAxisRanges).length ? { customAxisRanges } : {}),
    ...(shared ? { sharedAxisRange: shared } : {}),
  }
}

/** The preview as the Process Data workbench loads it, keyed by chart pen ids. */
export function toComputedRecord(
  trend: TrendPreview,
  siteId: string
): ComputedTrendRecord {
  return {
    key: trend.sessionKey
      ? `chat-${trend.sessionKey}`
      : computedTrendKey(siteId, trend.entryIds, {
          start: trend.startDatetime,
          end: trend.endDatetime,
        }),
    siteId,
    label: trend.label,
    startDatetime: trend.startDatetime,
    endDatetime: trend.endDatetime,
    contextEntryIds: trend.computedSeries?.length
      ? trend.contextEntryIds
      : (trend.contextEntryIds ?? trend.entryIds),
    marker: trend.marker,
    series: (trend.computedSeries ?? []).map((series) => ({
      entryId: series.entry_id,
      label: series.label,
      units: series.units ?? undefined,
      baseEntryId: series.base_entry_id ?? undefined,
      points: series.points,
    })),
    ...chartAxisRanges(trend.yRanges),
    hiddenEntryIds: trend.hiddenPens,
    interval: trend.interval,
    intervalUnit: trend.intervalUnit,
  }
}

export function TrendPreviewPanel({
  trend,
  siteId,
  onClose,
  onViewChange,
}: {
  trend: TrendPreview
  siteId: string
  onClose: () => void
  onViewChange?: (view: EmbeddedTrendView) => void
}): React.ReactElement {
  useLingui()
  const openComputedTrend = useOpenComputedTrendResource()
  const renderWorkbench = useEmbeddedTrendWorkbench()
  const computedRecord = useMemo(
    () => toComputedRecord(trend, siteId),
    [siteId, trend]
  )
  useEffect(() => {
    if (computedRecord) saveComputedTrend(computedRecord)
  }, [computedRecord])

  const handleOpenInProcessData = useCallback(() => {
    openComputedTrend(computedRecord)
  }, [computedRecord, openComputedTrend])

  return (
    <PanelRoot>
      <PanelHeader>
        <Text variant='body' weight='medium' truncate>
          {trend.label}
        </Text>
        <Button
          type='button'
          variant='ghost'
          size='xs'
          aria-label={t`Close trend preview`}
          onClick={onClose}>
          <X />
        </Button>
      </PanelHeader>
      <ChartArea>
        {renderWorkbench
          ? renderWorkbench({
              siteId,
              sensorIds: computedRecord.contextEntryIds,
              start: trend.startDatetime,
              end: trend.endDatetime,
              computedKey: computedRecord.key,
              onViewChange,
            })
          : null}
      </ChartArea>
      <ActionFooter>
        <Button
          variant='primaryRaised'
          size='lg'
          onClick={handleOpenInProcessData}>
          <Trans>Open in Process Data</Trans>
          <ExternalLink />
        </Button>
      </ActionFooter>
    </PanelRoot>
  )
}
